import axios from 'axios';
import { api, refreshAccessToken } from './client';
import { tokenStorage } from '@/services/storage/tokenStorage';
import { LoginCredentials, AuthResponse, User, RefreshTokenResponse } from '@/types/auth';

export class AuthApi {
  /**
   * Вход в систему
   */
  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Причину неудачного входа показывает сама форма — без второго сообщения тостом
    const response = await api.post<AuthResponse>('/auth/login', credentials, { skipErrorToast: true });
    return (response.data ?? response) as any;
  }

  /**
   * Обновление токена по сохранённому refresh-токену — то же общее обновление, что и в интерцепторе API:
   * два параллельных обновления одним refresh-токеном выбивали пользователя из сессии.
   * Новые токены уже сохранены в хранилище.
   */
  static async refreshToken(): Promise<RefreshTokenResponse> {
    const accessToken = await refreshAccessToken();
    return { accessToken, refreshToken: tokenStorage.getRefreshToken() ?? '' };
  }

  /**
   * Выход из системы
   */
  static async logout(refreshToken?: string): Promise<void> {
    await api.post('/auth/logout', { refreshToken });
  }

  /**
   * Завершить серверную сессию при выходе (AUTH-3): иначе refresh-токен оставался рабочим ещё 30 дней.
   * Без интерсепторов API: выход не показывает ошибок и не пытается обновить токен —
   * локальные токены к этому моменту уже очищены. Токены передаются явно.
   */
  static async endSession(accessToken: string | null, refreshToken: string | null): Promise<void> {
    if (!refreshToken) return;
    const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
    await axios.post(
      `${baseURL}/auth/logout`,
      { refreshToken },
      { headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined, timeout: 5000 },
    );
  }

  /**
   * Выход из всех устройств
   */
  static async logoutAll(): Promise<void> {
    await api.post('/auth/logout-all');
  }

  /**
   * Получение профиля пользователя
   */
  static async getProfile(): Promise<User> {
    const response = await api.get<User>('/auth/profile');
    return (response.data ?? response) as any;
  }

  /**
   * Обновление профиля пользователя
   */
  static async updateProfile(data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
  }): Promise<User> {
    const response = await api.put<User>('/auth/profile', data);
    return (response.data ?? response) as any;
  }

  /**
   * Изменение пароля
   */
  static async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> {
    await api.put('/auth/change-password', data);
  }

  /**
   * Проверка состояния аутентификации
   */
  static async checkAuth(): Promise<{ isAuthenticated: boolean; user: User | null }> {
    const response = await api.get<{ isAuthenticated: boolean; user: User | null }>('/auth/check');
    return (response.data ?? response) as any;
  }
}

export const authApi = AuthApi;
